import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import DatePicker from "react-datepicker";
import Select from 'react-select';
import "react-datepicker/dist/react-datepicker.css";

import { PrivateAxiosFile } from '../../../environment/AxiosInstance';
import { ErrorMessage, SuccessMessage } from '../../../environment/ToastMessage';
import { UserAuth } from '../../auth/Auth';

const EditTaskModal = ({ show, handleClose, task, onUpdated }) => {
    const { User, MatchPermission } = UserAuth();

    // ================== ALL HOOKS MUST BE AT THE TOP ==================
    const [taskName, setTaskName] = useState("");
    const [doer, setDoer] = useState([]);
    const [dateRange, setDateRange] = useState([null, null]);
    const [priority, setPriority] = useState(null);
    const [description, setDescription] = useState("");
    const [uploadFiles, setUploadFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isDarkTheme, setIsDarkTheme] = useState(false);

    const [startDate, endDate] = dateRange;
    const priorityOptions = [
        { id: "high", name: "High" },
        { id: "medium", name: "Medium" },
        { id: "low", name: "Low" },
    ];

    // Theme Observer
    useEffect(() => {
        const checkTheme = () => {
            setIsDarkTheme(document.body.classList.contains("dark-theme"));
        };
        checkTheme();
        const observer = new MutationObserver(checkTheme);
        observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
        return () => observer.disconnect();
    }, []);

    // Populate fields on modal open
    useEffect(() => {
        if (!task) return;

        setTaskName(task.task_name || "");
        setDescription(task.description || "");
        setDateRange([
            task.start_date ? new Date(task.start_date) : null,
            task.end_date ? new Date(task.end_date) : null
        ]);
        
        let assignedToIds = [];
        if (Array.isArray(task.assigned_to)) {
            assignedToIds = task.assigned_to.map(Number);
        } else if (typeof task.assigned_to === "string") {
            try {
                assignedToIds = JSON.parse(task.assigned_to).map(Number);
            } catch {
                assignedToIds = task.assigned_to.split(",").map(id => Number(id));
            }
        }

        const selectedUsers = User?.filter(u => assignedToIds.includes(u.id)) || [];
        setDoer(selectedUsers);
        
        setPriority(priorityOptions?.find(p => p.id === task.priority) || null);
    }, [task]);

    // ================== PERMISSION CHECK AFTER ALL HOOKS ==================
    // Only allow users with Edit Project permission
    if (!MatchPermission(['Edit Project', 'Project All', 'Doer Edit Project'])) {
        return null;
    }

    const handleUpdateTask = async () => {
        if (!MatchPermission(['Edit Project', 'Project All', 'Doer Edit Project'])) {
            ErrorMessage("You don't have permission to edit tasks");
            return;
        }

        if (!taskName || !doer || !dateRange[0] || !dateRange[1] || !priority) {
            alert("Please fill all required fields");
            return;
        }

        setLoading(true);

        try {
            const formData = new FormData();
            formData.append("task_name", taskName);
            formData.append("assigned_to", doer.map(d => d.id).join(","));
            formData.append("start_date", dateRange[0].toISOString());
            formData.append("end_date", dateRange[1].toISOString());
            formData.append("priority", priority.id || priority);
            formData.append("description", description);

            // Append uploaded files if any
            if (uploadFiles.length > 0) {
                formData.append("file", uploadFiles[0]); 
            }

            const res = await PrivateAxiosFile.put(`/projecttasks/${task.id}/update`, formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });

            if (res.data?.success) {
                SuccessMessage("Task updated successfully");
                if (onUpdated) onUpdated(); // refresh parent list
                handleClose();
            } else {
                console.error("Failed to update task:", res.data?.message);
            }

        } catch (err) {
            console.error("Error updating task:", err);
        }

        setLoading(false);
    };

    //===============================Render=========================
    return (
        <Modal id="EditPTaskModal" show={show} onHide={handleClose} backdrop="static" centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title className="gth-modal-title">Edit Task</Modal.Title>
            </Modal.Header>
            <Modal.Body className='pb-1'>
                <div className="row">
                    {/* Task Name */}
                    <div className="col-lg-12">
                        <div className="form-group">
                            <label className="form-label">Task Name</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Task Name"
                                value={taskName}
                                onChange={(e) => setTaskName(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Doer */}
                    <div className="col-lg-6">
                        <div className="form-group">
                            <label className="form-label">Doer</label>
                           <Select
                                isMulti
                                options={User}
                                getOptionLabel={option => option.name}
                                getOptionValue={option => option.id}
                                value={doer}
                                onChange={setDoer}
                                theme={theme => ({ ...theme, colors: { ...theme.colors, primary25: "#ddddff", primary: "#6161ff" } })}
                                styles={{
                                    menu: base => ({ ...base, backgroundColor: isDarkTheme ? "#06162f" : "#ffffff", color: isDarkTheme ? "#e0e0e0" : "#000000" }),
                                    multiValueLabel: base => ({ ...base, color: "#000000" }),
                                }}
                            />
                        </div>
                    </div>

                    {/* Start Date */}
                    <div className="col-lg-6">
                        <div className="form-group">
                            <label className="form-label">Task Start Date</label>
                            <div className="exp-datepicker-cont">
                                <span className="cal-icon"><i className="fas fa-calendar-alt"></i></span>
                                <DatePicker
                                    selected={startDate}
                                    onChange={(date) => setDateRange([date, endDate])}
                                    placeholderText="Select Start Date"
                                />
                            </div>
                        </div>
                    </div>

                    {/* End Date */}
                    <div className="col-lg-6">
                        <div className="form-group">
                            <label className="form-label">Task End Date</label>
                            <div className="exp-datepicker-cont">
                                <span className="cal-icon"><i className="fas fa-calendar-alt"></i></span>
                                <DatePicker
                                    selected={endDate}
                                    onChange={(date) => setDateRange([startDate, date])}
                                    placeholderText="Select End Date"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Priority */}
                    <div className="col-lg-6">
                        <div className="form-group">
                            <label className="form-label">Priority</label>
                            <Select
                                options={priorityOptions}
                                getOptionLabel={(option) => option.name}
                                getOptionValue={(option) => option.id}
                                value={priority}
                                onChange={setPriority}
                            />
                        </div>
                    </div>

                    {/* Description */}
                    <div className="col-lg-12">
                        <div className="form-group">
                            <label className="form-label">Description</label>
                            <textarea
                                className="form-control"
                                rows={3}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            ></textarea>
                        </div>
                    </div>

                    {/* Upload */}
                    <div className="col-12">
                        <div className="form-group">
                            <input type="file" onChange={(e) => setUploadFiles(e.target.files)} className='form-control' multiple />
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="gth-blue-light-bg">
                <button type="button" className="btn" onClick={handleClose}>
                    Cancel
                </button>
                <button
                    type="button"
                    className="btn btn-exp-green"
                    onClick={handleUpdateTask}
                    disabled={loading}
                >
                    {loading ? "Updating..." : "Update"}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default EditTaskModal;
